// ===========================================
// Account Settings Page
// ===========================================
// Account management: profile, password, data export, account deletion
// Supports fun mode styling for students

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, Box, Card, CardContent, Skeleton, Snackbar } from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../contexts/useAuth';
import { useStudentLayout } from '../../contexts/StudentLayoutContext';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import { useMyStudentProfile } from '../../hooks/useStudentProfile';
import {
  useDeletionBlockers,
  useDeletionSummary,
  useRequestDeletion,
  useExportUserData,
} from '../../hooks/useAccountDeletion';
import {
  // Types
  type UserProfile,
  type ProfileFormData,
  type SnackbarState,
  // Constants
  DEFAULT_PROFILE_FORM,
  DEFAULT_SNACKBAR_STATE,
  // Helpers
  getAccountBreadcrumbs,
  // Hooks
  useUserProfile,
  useUpdateProfile,
  useChangePassword,
  // Components
  ProfileSection,
  PhoneVerificationSection,
  PasswordSection,
  CalendarIntegrationSection,
  TwoFactorSection,
  DataExportSection,
  DeletionSection,
  ChangePasswordDialog,
  AccountDeletionDialog,
  StudentFunModeWrapper,
  ConsentPreferencesSection,
} from '../../components/settings/account';

// ===========================================
// COMPONENT
// ===========================================

export default function AccountSettingsPage() {
  const { user, logout, refreshUser } = useAuth();
  const { layoutMode } = useStudentLayout();
  const navigate = useNavigate();
  const location = useLocation();

  // Role checks
  const isStudent = user?.role === 'STUDENT';
  const isTeacher = user?.role === 'TEACHER';
  const isFunMode = isStudent && layoutMode === 'fun';

  // Student profile for fun mode header
  const { data: studentProfile } = useMyStudentProfile({ enabled: isStudent });

  // Profile state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileFormData>(DEFAULT_PROFILE_FORM);

  // Dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Feedback state
  const [snackbar, setSnackbar] = useState<SnackbarState>(DEFAULT_SNACKBAR_STATE);

  // Queries
  const { data: profileData, isLoading: isLoadingProfile } = useUserProfile();
  const {
    data: blockersData,
    isLoading: isLoadingBlockers,
    refetch: refetchBlockers,
  } = useDeletionBlockers();
  const { data: summaryData, isLoading: isLoadingSummary } = useDeletionSummary(showDeleteDialog);

  // Mutations
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const requestDeletion = useRequestDeletion();
  const exportData = useExportUserData();

  // Derived state
  const apiProfile = profileData as UserProfile | undefined;
  const profile =
    apiProfile ||
    (user
      ? {
          id: '',
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: null,
          role: user.role,
          isActive: true,
          emailVerified: true,
          createdAt: '',
          lastPasswordChange: null,
          school: { id: user.schoolId, name: user.schoolName, slug: '' },
        }
      : null);
  const blockers = blockersData?.data?.blockers || [];
  const canDelete = blockersData?.data?.canDelete ?? false;
  const summary = summaryData?.data;

  // Profile handlers
  const handleStartEditProfile = () => {
    if (profile) {
      setProfileForm({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone || '',
      });
      setIsEditingProfile(true);
    } else if (user) {
      setProfileForm({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: '',
      });
      setIsEditingProfile(true);
    }
  };

  const handleCancelEditProfile = () => {
    setIsEditingProfile(false);
    setProfileForm(DEFAULT_PROFILE_FORM);
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile.mutateAsync(profileForm);
      await refreshUser();
      setIsEditingProfile(false);
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to update profile', severity: 'error' });
    }
  };

  // Export handler
  const handleExportData = async () => {
    try {
      await exportData.mutateAsync();
      setSnackbar({
        open: true,
        message: 'Your data has been exported and downloaded',
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Failed to export data', severity: 'error' });
    }
  };

  // Delete dialog handler
  const handleOpenDeleteDialog = () => {
    setShowDeleteDialog(true);
    refetchBlockers();
  };

  const handleDeletionComplete = async () => {
    await logout();
    const schoolSlug = extractSchoolSlugFromPath(location.pathname);
    navigate(schoolSlug ? `/${schoolSlug}/login` : '/login');
  };

  // Loading state
  if (isLoadingProfile || isLoadingBlockers) {
    return (
      <Box>
        <PageHeader
          title="Account Settings"
          subtitle="Manage your account and data"
          breadcrumbs={getAccountBreadcrumbs()}
        />
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={300} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Settings content - shared between both modes
  const settingsContent = (
    <>
      {/* Page header for non-fun mode */}
      {!isFunMode && (
        <PageHeader
          title="Account Settings"
          subtitle="Manage your account and data"
          breadcrumbs={getAccountBreadcrumbs()}
        />
      )}

      {/* Profile Section (includes integrated avatar for non-students) */}
      <ProfileSection
        profile={profile}
        isEditing={isEditingProfile}
        formData={profileForm}
        isFunMode={isFunMode}
        isUpdating={updateProfile.isPending}
        isStudent={isStudent}
        onStartEdit={handleStartEditProfile}
        onCancelEdit={handleCancelEditProfile}
        onSave={handleSaveProfile}
        onFormChange={setProfileForm}
      />

      {/* Phone Verification Section (shown when phone not verified) */}
      <PhoneVerificationSection isFunMode={isFunMode} />

      {/* Consent Preferences Section - primary parents only */}
      {user?.role === 'PARENT' && apiProfile?.Parent?.isPrimary && apiProfile.Parent.familyId && (
        <ConsentPreferencesSection
          familyId={apiProfile.Parent.familyId}
          consentAccepted={apiProfile.Parent.Family?.consentAccepted ?? false}
          consentAcceptedAt={apiProfile.Parent.Family?.consentAcceptedAt ?? null}
          mediaExposure={apiProfile.Parent.Family?.mediaExposure ?? 'PENDING'}
          isFunMode={isFunMode}
        />
      )}

      {/* Calendar Integration Section */}
      <CalendarIntegrationSection isFunMode={isFunMode} />

      {/* Password Section */}
      <PasswordSection
        lastPasswordChange={profile?.lastPasswordChange}
        isFunMode={isFunMode}
        onOpenPasswordDialog={() => setShowPasswordDialog(true)}
      />

      {/* Two-Factor Authentication Section */}
      <TwoFactorSection isFunMode={isFunMode} />

      {/* Data Export Section - hidden for teachers */}
      {!isTeacher && (
        <DataExportSection
          isFunMode={isFunMode}
          isExporting={exportData.isPending}
          onExport={handleExportData}
        />
      )}

      {/* Deletion Section - hidden for teachers */}
      {!isTeacher && (
        <DeletionSection
          blockers={blockers}
          canDelete={canDelete}
          isFunMode={isFunMode}
          onOpenDeleteDialog={handleOpenDeleteDialog}
        />
      )}

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        changePasswordMutation={changePassword}
        onSuccess={() =>
          setSnackbar({
            open: true,
            message: 'Password changed successfully',
            severity: 'success',
          })
        }
        onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
      />

      {/* Deletion Dialog */}
      <AccountDeletionDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        blockers={blockers}
        canDelete={canDelete}
        isLoadingBlockers={isLoadingBlockers}
        summary={summary}
        isLoadingSummary={isLoadingSummary}
        requestDeletionMutation={requestDeletion}
        onDeletionComplete={handleDeletionComplete}
      />

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  // Fun mode wrapper for students
  if (isFunMode && studentProfile) {
    return (
      <StudentFunModeWrapper studentProfile={studentProfile} onLogout={logout}>
        {settingsContent}
      </StudentFunModeWrapper>
    );
  }

  // Standard layout for non-students or classic mode
  return <Box>{settingsContent}</Box>;
}
